// src/features/locker-assignments/components/ManageTagsDialog.tsx

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  TextField,
  Stack,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Receipt as QrCodeIcon,
  CreditCard as CardIcon,
  Numbers as NumbersIcon,
  Fingerprint as FingerprintIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { useTranslate, useNotify, useDataProvider } from 'react-admin';

// Interface para TagType basada en el backend
interface TagType {
  id: string;
  typeCode: string;
  name: string;
  isSystemGenerated: boolean;
  requiresManualInput: boolean;
  description?: string;
}

// Interface para Tag
interface Tag {
  id: string;
  code: string;
  data?: string;
  typeCode: string;
  typeName?: string;
  description?: string;
  status: 'Active' | 'Inactive';
  createdAt?: string;
}

interface ManageTagsDialogProps {
  open: boolean;
  onClose: () => void;
  assignmentId: string;
  assignmentReference: string;
  currentTags: Tag[];
  onTagsUpdated?: (tags: Tag[]) => void;
  hotelId: string; 
}

export const ManageTagsDialog: React.FC<ManageTagsDialogProps> = ({
  open,
  onClose,
  assignmentId,
  assignmentReference,
  currentTags: initialTags,
  onTagsUpdated,
  hotelId,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  
  const [tags, setTags] = useState<Tag[]>(initialTags);
  const [loading, setLoading] = useState(false);
  const [addingTag, setAddingTag] = useState(false);
  const [deletingTagId, setDeletingTagId] = useState<string | null>(null); // Para mostrar loading individual
  const [newTagCode, setNewTagCode] = useState('');
  const [newTagDescription, setNewTagDescription] = useState('');
  const [selectedTagTypeId, setSelectedTagTypeId] = useState<string>('');
  const [availableTagTypes, setAvailableTagTypes] = useState<TagType[]>([]);
  const [loadingTagTypes, setLoadingTagTypes] = useState(false);
  const [typesLoaded, setTypesLoaded] = useState(false);

  useEffect(() => {
    setTags(initialTags);
  }, [initialTags]);

  useEffect(() => {
    const loadTagTypes = async () => {
      if (!open || !hotelId || loadingTagTypes || typesLoaded) return;
      
      setLoadingTagTypes(true);
      
      try {
        const response = await (dataProvider as any).getGeneralConfiguration(hotelId);
        
        const supportedTagTypes = response.data?.supportedTagTypes || [];
        
        setAvailableTagTypes(supportedTagTypes);
        setTypesLoaded(true);
        
        // Seleccionar el primer tipo por defecto
        if (supportedTagTypes.length > 0) {
          setSelectedTagTypeId(supportedTagTypes[0].id);
        }
      } catch (error) {
        console.error('🏷️ Error loading tag types:', error);
        notify('Error loading tag types', { type: 'error' });
        setAvailableTagTypes([]);
        setTypesLoaded(true);
      } finally {
        setLoadingTagTypes(false);
      }
    };

    loadTagTypes();
  }, [open, hotelId, dataProvider, notify]);

  useEffect(() => {
    if (!open) {
      setTypesLoaded(false);
      setSelectedTagTypeId('');
      setNewTagCode('');
      setNewTagDescription('');
    }
  }, [open]);

  // Obtener el tipo seleccionado
  const selectedTagType = availableTagTypes.find(t => t.id === selectedTagTypeId);
  const requiresManualInput = selectedTagType?.requiresManualInput !== false;
  const isAutoGenerated = !requiresManualInput;

  const handleAddTag = async () => {
    // Validación solo si el tipo requiere entrada manual
    if (requiresManualInput && !isAutoGenerated && !newTagCode.trim()) {
      notify(translate('resources.locker-assignments.tags.error_empty_code'), { type: 'warning' });
      return;
    }

    if (!selectedTagTypeId) {
      notify(translate('resources.locker-assignments.tags.error_no_type_selected'), { type: 'warning' });
      return;
    }

    setAddingTag(true);

    try {
      // Preparar datos para enviar
      const tagData: any = {
        tagTypeId: selectedTagTypeId,
        description: newTagDescription || undefined,
      };

      // Solo incluir código si no es autogenerado
      if (!isAutoGenerated && requiresManualInput) {
        tagData.data = newTagCode;
      }

      // LLAMADA REAL A LA API para agregar tag usando tu dataProvider
      const response = await (dataProvider as any).createAssignmentTag(
        assignmentId,
        tagData
      );

      // Usar la respuesta real del servidor
      const newTag: Tag = {
        id: response.data.id || response.data.tagId,
        code: response.data.code || response.data.data || newTagCode,
        data: response.data.data || newTagCode,
        typeCode: response.data.typeCode || selectedTagType?.typeCode || '',
        typeName: response.data.typeName || selectedTagType?.name || '',
        description: response.data.description || newTagDescription || undefined,
        status: response.data.isActive !== false ? 'Active' : 'Inactive',
        createdAt: response.data.createdAt || new Date().toISOString(),
      };
      
      
     

      const updatedTags = [...tags, newTag];
      setTags(updatedTags);
      
      // Limpiar campos
      setNewTagCode('');
      setNewTagDescription('');
      
      notify(translate('resources.locker-assignments.tags.added_successfully'), { type: 'success' });
      
      if (onTagsUpdated) {
        onTagsUpdated(updatedTags);
      }
        // Cerrar el diálogo si es el primer tag
      if (tags.length === 0) {
        onClose();
      }

    } catch (error: any) {
      console.error('Error adding tag:', error);
      const errorMessage = error?.body?.message || error?.message || 
                          translate('resources.locker-assignments.tags.error_adding');
      notify(errorMessage, { type: 'error' });
    } finally {
      setAddingTag(false);
    }
  };

  // Auto-select the only available tag type (if exactly one)
useEffect(() => {
  if (availableTagTypes.length === 1) {
    setSelectedTagTypeId(availableTagTypes[0].id);
  }
}, [availableTagTypes]);


  const handleRemoveTag = async (tagId: string) => {
    setDeletingTagId(tagId);
    try {
      // LLAMADA REAL A LA API para eliminar tag usando tu dataProvider
      await (dataProvider as any).deleteAssignmentTag(tagId, hotelId);

      // Actualizar el estado local solo después del éxito
      const updatedTags = tags.filter(tag => tag.id !== tagId);
      setTags(updatedTags);
      
      notify(translate('resources.locker-assignments.tags.removed_successfully'), { type: 'success' });
      
      if (onTagsUpdated) {
        onTagsUpdated(updatedTags);
      }
    } catch (error: any) {
      console.error('Error removing tag:', error);
      const errorMessage = error?.body?.message || error?.message || 
                          translate('resources.locker-assignments.tags.error_removing');
      notify(errorMessage, { type: 'error' });
    } finally {
      setDeletingTagId(null);
    }
  };

  const getTagIcon = (typeCode: string) => {
    switch (typeCode) {
      case 'RFID':
      case 'NFC_UID':
        return <CardIcon fontSize="small" />;
      case 'AUTO_NUMERIC':
      case 'NUMERIC_CODE':
        return <NumbersIcon fontSize="small" />;
      case 'QR_CODE':
        return <QrCodeIcon fontSize="small" />;
      case 'BARCODE':
        return <CodeIcon fontSize="small" />;
      case 'BIOMETRIC':
        return <FingerprintIcon fontSize="small" />;
      default:
        return <QrCodeIcon fontSize="small" />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
   
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QrCodeIcon />
            <Typography variant="h6">
              {translate('resources.locker-assignments.tags.manage_title')}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {translate('resources.locker-assignments.tags.assignment')}: {assignmentReference}
        </Typography>
      </DialogTitle>

      <DialogContent dividers >
        <Box sx={{ width: '80%', mx: 'auto'}}>
          {/* Current Tags Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              {translate('resources.locker-assignments.tags.current_tags')} ({tags.length})
            </Typography>
            
            {tags.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                {translate('resources.locker-assignments.tags.no_tags_yet')}
              </Typography>
            ) : (
              <List dense>
                {tags.map((tag) => (
                  <ListItem key={tag.id} sx={{ bgcolor: 'grey.50', mb: 1, borderRadius: 1 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getTagIcon(tag.typeCode)}
                          <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                            {tag.code || tag.data}
                          </Typography>
                          {tag.typeName && (
                            <Chip
                              label={tag.typeName}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          
                        </Box>
                      }
                      secondary={
                        <Box>
                          {tag.description && (
                            <Typography variant="body2" color="text.secondary">
                              {tag.description}
                            </Typography>
                          )}
                          {tag.createdAt && (
                            <Typography variant="caption" color="text.secondary">
                              {translate('resources.locker-assignments.tags.added_on', {
                                date: new Date(tag.createdAt).toLocaleDateString()
                              })}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveTag(tag.id)}
                        disabled={deletingTagId === tag.id}
                        size="small"
                        color="error"
                      >
                        {deletingTagId === tag.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          {/* Add New Tag Section */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {translate('resources.locker-assignments.tags.add_new')}
            </Typography>
            
            <Stack spacing={2}>
              {availableTagTypes.length > 1 && (
                <FormControl fullWidth>
                  <InputLabel id="tag-type-label">
                    {translate('resources.locker-assignments.tags.tag_type')}
                  </InputLabel>
                  <Select
                    labelId="tag-type-label"
                    value={selectedTagTypeId}
                    onChange={(e) => setSelectedTagTypeId(e.target.value)}
                    label={translate('resources.locker-assignments.tags.tag_type')}
                    disabled={addingTag}
                  >
                    {availableTagTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getTagIcon(type.typeCode)}
                          <span>{type.name}</span>
        
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {selectedTagType?.description && (
                    <FormHelperText>{selectedTagType.description}</FormHelperText>
                  )}
                </FormControl>
              )}

              {/* Mostrar loading o mensaje de no tag types */}
              {loadingTagTypes && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    {translate('resources.locker-assignments.tags.loading_types')}
                  </Typography>
                </Box>
              )}

              {!loadingTagTypes && availableTagTypes.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  {translate('resources.locker-assignments.tags.no_types_configured')}
                </Typography>
              )}

              {/* Show tag code field only if not auto-generated */}
              {!isAutoGenerated && requiresManualInput && (
                <TextField
                  fullWidth
                  label={translate('resources.locker-assignments.tags.tag_code')}
                  value={newTagCode}
                  onChange={(e) => setNewTagCode(e.target.value)}
                  placeholder={translate('resources.locker-assignments.tags.enter_code')}
                  disabled={addingTag}
                  required
                  InputProps={{
                    sx: { fontFamily: 'monospace' }
                  }}
                  helperText={translate('resources.locker-assignments.tags.code_help')}
                />
              )}


              {/* Description field */}
              <TextField
                label={translate('resources.locker-assignments.tags.description')}
                value={newTagDescription}
                onChange={(e) => setNewTagDescription(e.target.value)}
                placeholder={translate('resources.locker-assignments.tags.description_placeholder')}
                disabled={addingTag}
                multiline
                rows={2}
                helperText={translate('resources.locker-assignments.tags.description_help')}
              
              />
            </Stack>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions >
              <Button
                variant="contained"
                startIcon={addingTag ? <CircularProgress size={20} /> : <AddIcon />}
                onClick={handleAddTag}
                disabled={
                  addingTag || 
                  !selectedTagTypeId ||
                  (!isAutoGenerated && requiresManualInput && !newTagCode.trim())
                }
              >
                {translate('resources.locker-assignments.tags.add_tag')}
              </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageTagsDialog;